<template>
    <div class="settings-page">
        <div class="settings-container">
            <!-- Sidebar -->
            <div class="settings-sidebar">
                <nav class="sidebar-nav">
                    <router-link
                        v-for="item in sidebarItems"
                        :key="item.link"
                        :to="`/settings/${item.link}`"
                        class="sidebar-item"
                        :class="{ 'active': currentItem.link == item.link }"
                    >
                        <vue-feather :type="item.icon" size="18" />
                        <span>{{ item.title }}</span>
                    </router-link>
                </nav>
            </div>

            <!-- Main Content -->
            <div class="settings-content">
                <div class="settings-header">
                    <h1>{{ currentItem.title }}</h1>
                    <p class="settings-description">{{ getDescription(currentItem.link) }}</p>
                </div>

                <div v-if="!userModel" class="loading-state">
                    <vue-feather type="loader" size="24" animation="spin" />
                    <span>Loading settings...</span>
                </div>

                <div v-else class="settings-form">
                    <!-- General Settings -->
                    <div v-if="currentItem.link == 'general'" class="form-section">
                        <div class="form-row">
                            <div class="form-group">
                                <label for="username">Username</label>
                                <div class="input-wrapper">
                                    <vue-feather type="at-sign" size="16" />
                                    <input id="username" v-model="userModel.username" readonly disabled />
                                </div>
                            </div>
                            <div class="form-group">
                                <label for="displayName">Display Name</label>
                                <div class="input-wrapper">
                                    <vue-feather type="user" size="16" />
                                    <input id="displayName" v-model="userModel.displayName" placeholder="Enter display name" />
                                </div>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="phone">Phone Number</label>
                                <div class="input-wrapper">
                                    <vue-feather type="phone" size="16" />
                                    <input id="phone" v-model="userModel.phone" placeholder="Enter phone number" />
                                </div>
                            </div>
                            <div class="form-group">
                                <label for="email">Email</label>
                                <div class="input-wrapper">
                                    <vue-feather type="mail" size="16" />
                                    <input id="email" v-model="userModel.email" placeholder="Enter email" />
                                </div>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="country">Country</label>
                                <div class="select-wrapper">
                                    <vue-feather type="globe" size="16" />
                                    <select id="country" v-model="userModel.country">
                                        <option :value="country.value" v-for="country in countries" :key="country.value">
                                            {{ country.label }}
                                        </option>
                                    </select>
                                    <vue-feather type="chevron-down" size="16" class="select-arrow" />
                                </div>
                            </div>
                            <div class="form-group">
                                <label for="gender">Gender</label>
                                <div class="select-wrapper">
                                    <vue-feather type="users" size="16" />
                                    <select id="gender" v-model="userModel.gender">
                                        <option value="0">Select gender</option>
                                        <option value="1">Male</option>
                                        <option value="2">Female</option>
                                    </select>
                                    <vue-feather type="chevron-down" size="16" class="select-arrow" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Profile Settings -->
                    <div v-if="currentItem.link == 'profile'" class="form-section">
                        <div class="form-row">
                            <div class="form-group">
                                <label for="firstName">First Name</label>
                                <div class="input-wrapper">
                                    <vue-feather type="user" size="16" />
                                    <input id="firstName" v-model="userModel.firstName" placeholder="Enter first name" />
                                </div>
                            </div>
                            <div class="form-group">
                                <label for="lastName">Last Name</label>
                                <div class="input-wrapper">
                                    <vue-feather type="user" size="16" />
                                    <input id="lastName" v-model="userModel.lastName" placeholder="Enter last name" />
                                </div>
                            </div>
                        </div>

                        <div class="form-group full-width">
                            <label for="bio">Bio</label>
                            <textarea id="bio" v-model="userModel.bio" rows="4" placeholder="Tell us about yourself..."></textarea>
                        </div>
                    </div>

                    <!-- Delete Account -->
                    <div v-if="currentItem.link == 'delete'" class="form-section">
                        <div class="warning-box">
                            <vue-feather type="alert-triangle" size="20" />
                            <div>
                                <strong>Warning:</strong> This action cannot be undone. All your data will be permanently deleted.
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="currentPasswordDelete">Confirm your password</label>
                            <div class="input-wrapper">
                                <vue-feather type="lock" size="16" />
                                <input id="currentPasswordDelete" type="password" v-model="userModel.currentPassword" placeholder="Enter your password" />
                            </div>
                        </div>
                    </div>

                    <!-- Password Settings -->
                    <div v-if="currentItem.link == 'password'" class="form-section">
                        <div class="form-group">
                            <label for="currentPassword">Current Password</label>
                            <div class="input-wrapper">
                                <vue-feather type="lock" size="16" />
                                <input id="currentPassword" type="password" v-model="userModel.currentPassword" placeholder="Enter current password" />
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="newPassword">New Password</label>
                            <div class="input-wrapper">
                                <vue-feather type="key" size="16" />
                                <input id="newPassword" type="password" v-model="userModel.newPassword" placeholder="Enter new password" />
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="confirmPassword">Confirm New Password</label>
                            <div class="input-wrapper">
                                <vue-feather type="key" size="16" />
                                <input id="confirmPassword" type="password" v-model="userModel.confirmPassword" placeholder="Confirm new password" />
                            </div>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="form-actions">
                        <button
                            v-if="!updated"
                            @click="saveUser"
                            :disabled="updating || isInvalidForm"
                            :class="['btn-primary', { 'btn-danger': currentItem.link == 'delete' }]"
                        >
                            <vue-feather v-if="updating" type="loader" size="18" animation="spin" />
                            <vue-feather v-else :type="currentItem.link == 'delete' ? 'trash-2' : 'check'" size="18" />
                            <span>{{ currentItem.link == 'delete' ? 'Delete Account' : 'Save Changes' }}</span>
                        </button>
                        <div v-else class="success-message">
                            <vue-feather type="check-circle" size="18" />
                            <span>Changes saved successfully!</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { API } from 'aws-amplify';
import { track as analyticsTrack } from '../utils/analytics';
import { updateUserModel } from '../graphql/mutations.js';
import { getUserModelByLegacyId, getUserModelForSettings, getUserModelForSettingsByUsername } from '../graphql/custom.js';
export default {
    data() {
        return {
            cognitoUser: null,
            userModel: null,
            updating: false,
            updated: false,
            sidebarItems: [
                {
                    title: "General Settings",
                    icon: "tool",
                    link: "general"
                },
                {
                    title: "Profile",
                    icon: "user",
                    link: "profile"
                },
                // {
                //     title: "Avatar & Cover",
                //     icon: "image",
                //     link: "avatar"
                // },
                // {
                //     title: "Verification",
                //     icon: "check-circle",
                //     link: "verification"
                // },
                // {
                //     title: "Blocked Users",
                //     icon: "slash",
                //     link: "blocked"
                // },
                {
                    title: "Delete account",
                    icon: "trash",
                    link: "delete"
                },
                {
                    title: "Password",
                    icon: "key",
                    link: "password"
                },
                // {
                //     title: "Two-factor Authentication",
                //     icon: "tablet",
                //     link: "two-factor"
                // },
                // {
                //     title: "My Information",
                //     icon: "hard-drive",
                //     link: "my-info"
                // },
            ]
        }
    },
    computed: {
        isInvalidForm() {
            return false
        },
        currentItem() {
            var tab = this.$route.path.replace(`/settings`, '').replace('/', '')
            if (tab == '') {
                tab = 'general'
            }
            let thisItem = this.sidebarItems.filter(item => item.link == tab)
            return thisItem[0]
        }
    },
    created() {
        this.fetchUser()
    },
    methods: {
        getDescription(link) {
            const descriptions = {
                general: 'Manage your account settings and preferences',
                profile: 'Update your public profile information',
                delete: 'Permanently delete your account and all associated data',
                password: 'Change your password to keep your account secure'
            };
            return descriptions[link] || '';
        },
        async fetchUser() {
            try {
                const user = await API.Auth.currentAuthenticatedUser()
                console.log('Current User Info', user)
                this.cognitoUser = user.attributes
                this.cognitoUser.username = user.username

                const parentModel = this.$parent?.currentUser?.model;
                if (parentModel?.id) {
                    this.userModel = this.normalizeUserModel(parentModel);
                    return;
                }

                const legacyUserId = this.cognitoUser['custom:legacyUserId'];
                if (legacyUserId) {
                    const res = await API.graphql({
                        query: getUserModelByLegacyId,
                        variables: { legacyId: legacyUserId }
                    });
                    const legacyModel = res.data.userByLegacyId?.items?.[0];
                    if (legacyModel) {
                        this.userModel = this.normalizeUserModel(legacyModel);
                        return;
                    }
                }

                const cognitoSub = this.cognitoUser.sub;
                if (cognitoSub) {
                    const res = await API.graphql({
                        query: getUserModelForSettings,
                        variables: { id: cognitoSub },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });
                    if (res.data.getUserModel) {
                        this.userModel = this.normalizeUserModel(res.data.getUserModel);
                        return;
                    }
                }

                const username = this.cognitoUser.preferred_username || this.cognitoUser.name || this.cognitoUser.username || user.username;
                if (username) {
                    const res = await API.graphql({
                        query: getUserModelForSettingsByUsername,
                        variables: { username },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });
                    const usernameModel = res.data.userByUsername?.items?.[0];
                    if (usernameModel) {
                        this.userModel = this.normalizeUserModel(usernameModel);
                        return;
                    }
                }

                this.userModel = this.normalizeUserModel({
                    id: cognitoSub,
                    username: user.username,
                    displayName: this.cognitoUser.name || user.username,
                    email: this.cognitoUser.email,
                    emailVerified: this.cognitoUser.email_verified === true || this.cognitoUser.email_verified === 'true'
                });
            } catch (error) {
                console.error('Error loading settings:', error);
                this.$toast.error('Failed to load settings. Please refresh and try again.');
                this.userModel = this.normalizeUserModel(this.$parent?.currentUser?.model || {});
            }
        },
        normalizeUserModel(userModel) {
            return {
                id: userModel?.id || '',
                username: userModel?.username || this.cognitoUser?.preferred_username || this.cognitoUser?.username || '',
                displayName: userModel?.displayName || this.cognitoUser?.name || userModel?.username || '',
                firstName: userModel?.firstName || '',
                lastName: userModel?.lastName || '',
                email: userModel?.email || this.cognitoUser?.email || '',
                phone: userModel?.phone || '',
                country: userModel?.country || '',
                gender: userModel?.gender || '0',
                bio: userModel?.bio || '',
                emailVerified: userModel?.emailVerified ?? (this.cognitoUser?.email_verified === true || this.cognitoUser?.email_verified === 'true'),
                legacyAvatar: userModel?.legacyAvatar || '',
                legacyCover: userModel?.legacyCover || '',
                legacyId: userModel?.legacyId || '',
                authOwner: userModel?.authOwner || ''
            };
        },
        async saveUser() {
            if (!this.userModel?.id) {
                this.$toast.error('Settings are still loading. Please try again.');
                return;
            }
            this.updating = true
            let userInput = {
                id: this.userModel.id,
                firstName: this.userModel.firstName,
                lastName: this.userModel.lastName,
                email: this.userModel.email,
                phone: this.userModel.phone,
                gender: this.userModel.gender,
                country: this.userModel.country,
                displayName: this.userModel.displayName,
                bio: this.userModel.bio
            }
            try {
                const fieldsChanged = Object.keys(userInput).filter(k => k !== 'id');
                analyticsTrack('profile_update_start', {
                    settings_section: this.currentItem.link,
                    fields_changed: fieldsChanged
                });
                const saveResponse = await API.graphql({
                    query: updateUserModel,
                    variables: { input: userInput },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                const savedUser = saveResponse.data.updateUserModel;
                this.userModel = { ...this.userModel, ...savedUser };
                if (this.$parent?.currentUser) {
                    this.$parent.currentUser.model = {
                        ...(this.$parent.currentUser.model || {}),
                        ...savedUser
                    };
                    this.$parent.currentUser.username = savedUser.username || this.$parent.currentUser.username;
                }
                this.updated = true
                analyticsTrack('profile_edit', {
                    settings_section: this.currentItem.link,
                    fields_changed: fieldsChanged
                });
                this.$toast.success('Settings saved successfully.');
                console.log('save response', saveResponse);
            } catch (error) {
                console.error('Error saving settings:', error);
                analyticsTrack('profile_update_failed', {
                    settings_section: this.currentItem.link,
                    error_code: this.getAnalyticsErrorCode(error)
                });
                this.$toast.error('Failed to save settings. Please try again.');
            } finally {
                this.updating = false
            }
        },
        getAnalyticsErrorCode(error) {
            const message = String(error?.errors?.[0]?.message || error?.message || error?.name || '').toLowerCase();
            if (!message) return 'unknown';
            if (message.includes('not authorized') || message.includes('unauthorized')) return 'unauthorized';
            if (message.includes('network') || message.includes('fetch')) return 'network_error';
            if (message.includes('timeout') || message.includes('timed out')) return 'timeout';
            if (message.includes('validation')) return 'validation_error';
            return 'error';
        }
    },
}
</script>

<style scoped>
.settings-page {
    min-height: calc(100vh - 60px);
    padding: 24px;
    background: var(--bg-primary, #0a0a0a);
}

.settings-container {
    max-width: 1100px;
    margin: 0 auto;
    display: flex;
    gap: 24px;
}

/* Sidebar */
.settings-sidebar {
    width: 260px;
    flex-shrink: 0;
}

.sidebar-nav {
    background: var(--bg-secondary, #1a1a1a);
    border-radius: 12px;
    padding: 8px;
    border: 1px solid var(--border-color, #333);
}

.sidebar-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 8px;
    text-decoration: none;
    color: var(--text-secondary, #888);
    transition: all 0.2s ease;
    margin-bottom: 4px;
}

.sidebar-item:last-child {
    margin-bottom: 0;
}

.sidebar-item:hover {
    background: var(--bg-tertiary, #252525);
    color: var(--text-primary, #fff);
}

.sidebar-item.active {
    background: var(--primary-color, #ff4444);
    color: white;
}

.sidebar-item span {
    font-size: 14px;
    font-weight: 500;
}

/* Main Content */
.settings-content {
    flex: 1;
    background: var(--bg-secondary, #1a1a1a);
    border-radius: 12px;
    padding: 32px;
    border: 1px solid var(--border-color, #333);
}

.settings-header {
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid var(--border-color, #333);
}

.settings-header h1 {
    font-size: 24px;
    font-weight: 600;
    color: var(--text-primary, #fff);
    margin: 0 0 8px 0;
}

.settings-description {
    font-size: 14px;
    color: var(--text-tertiary, #666);
    margin: 0;
}

/* Loading State */
.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px;
    color: var(--text-tertiary, #666);
}

/* Form Styles */
.form-section {
    margin-bottom: 24px;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
}

.form-row:last-child {
    margin-bottom: 0;
}

.form-row .form-group {
    margin-bottom: 0;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
}

.form-group:last-child {
    margin-bottom: 0;
}

.form-group.full-width {
    grid-column: 1 / -1;
}

.form-group label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary, #888);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.input-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--bg-tertiary, #252525);
    border: 1px solid var(--border-color, #333);
    border-radius: 8px;
    padding: 0 16px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-wrapper:focus-within {
    border-color: var(--primary-color, #ff4444);
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
}

.input-wrapper svg {
    color: var(--text-tertiary, #666);
    flex-shrink: 0;
}

.input-wrapper input {
    flex: 1;
    background: transparent;
    border: none;
    padding: 14px 0;
    font-size: 14px;
    color: var(--text-primary, #fff);
    outline: none;
}

.input-wrapper input::placeholder {
    color: var(--text-tertiary, #666);
}

/* Select Styles */
.select-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--bg-tertiary, #252525);
    border: 1px solid var(--border-color, #333);
    border-radius: 8px;
    padding: 0 16px;
    position: relative;
    transition: border-color 0.2s ease;
}

.select-wrapper:focus-within {
    border-color: var(--primary-color, #ff4444);
}

.select-wrapper svg {
    color: var(--text-tertiary, #666);
    flex-shrink: 0;
}

.select-wrapper select {
    flex: 1;
    background: transparent;
    border: none;
    padding: 14px 0;
    font-size: 14px;
    color: var(--text-primary, #fff);
    outline: none;
    appearance: none;
    cursor: pointer;
}

.select-wrapper .select-arrow {
    position: absolute;
    right: 16px;
    pointer-events: none;
}

/* Textarea */
.form-group textarea {
    background: var(--bg-tertiary, #252525);
    border: 1px solid var(--border-color, #333);
    border-radius: 8px;
    padding: 14px 16px;
    font-size: 14px;
    color: var(--text-primary, #fff);
    resize: vertical;
    min-height: 120px;
    outline: none;
    transition: border-color 0.2s ease;
    font-family: inherit;
}

.form-group textarea:focus {
    border-color: var(--primary-color, #ff4444);
}

.form-group textarea::placeholder {
    color: var(--text-tertiary, #666);
}

/* Warning Box */
.warning-box {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    background: rgba(255, 68, 68, 0.1);
    border: 1px solid rgba(255, 68, 68, 0.3);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 24px;
    color: var(--text-secondary, #ccc);
    font-size: 14px;
    line-height: 1.5;
}

.warning-box svg {
    color: var(--primary-color, #ff4444);
    flex-shrink: 0;
    margin-top: 2px;
}

.warning-box strong {
    color: var(--primary-color, #ff4444);
}

/* Action Buttons */
.form-actions {
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid var(--border-color, #333);
}

.btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--primary-color, #ff4444);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
    background: var(--primary-hover, #e03030);
    transform: translateY(-1px);
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-danger {
    background: #dc3545;
}

.btn-danger:hover:not(:disabled) {
    background: #c82333;
}

.success-message {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #28a745;
    font-size: 14px;
    font-weight: 500;
}

/* Responsive */
@media (max-width: 900px) {
    .settings-container {
        flex-direction: column;
    }

    .settings-sidebar {
        width: 100%;
    }

    .sidebar-nav {
        display: flex;
        overflow-x: auto;
        padding: 8px;
        gap: 4px;
    }

    .sidebar-item {
        flex-shrink: 0;
        margin-bottom: 0;
    }

    .settings-content {
        padding: 24px;
    }
}

@media (max-width: 600px) {
    .settings-page {
        padding: 16px;
    }

    .form-row {
        grid-template-columns: 1fr;
    }

    .settings-content {
        padding: 20px;
    }

    .settings-header h1 {
        font-size: 20px;
    }
}
</style>
